import React, { useEffect, useRef, useState } from 'react';

interface HandGestureOverlayProps {
  gesture: string | null;
  confidence: number;
  showRecovery?: boolean;
  onRestart?: () => void;
  progressFeedback?: string;
}

const GESTURE_LABELS: Record<string, string> = {
  Thumb_Up: 'Play',
  Closed_Fist: 'Pause',
  Victory: 'Toggle Instructions',
  Open_Palm: 'Toggle UI',
  Thumb_Down: 'Thumb Down',
  ILoveYou: 'I Love You',
  Pointing_Up: 'Pointing Up',
};

export function HandGestureOverlay({
  gesture,
  confidence,
  showRecovery = false,
  onRestart,
  progressFeedback,
}: HandGestureOverlayProps) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isActive = gesture !== null && gesture !== 'None';

  useEffect(() => {
    if (isActive) {
      setVisible(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setVisible(false);
      }, 1500);
    } else {
      setVisible(false);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [gesture, isActive]);

  const label = gesture ? GESTURE_LABELS[gesture] ?? gesture : null;
  const confidencePercent = `${Math.round(confidence * 100)}%`;

  // Render nothing when no gesture and no recovery
  if (!isActive && !showRecovery) return null;

  // Show recovery pill even when gesture is null
  if (!isActive && showRecovery) {
    return (
      <div>
        <button
          onClick={onRestart}
          className="rounded-full bg-muted px-3 py-1 text-sm text-muted-foreground"
        >
          Show Controls
        </button>
      </div>
    );
  }

  return (
    <div role="status" aria-live="polite" className="flex flex-col items-center gap-1">
      {visible && label && (
        <div className="flex items-center gap-2 rounded-full bg-background/80 px-3 py-1 text-sm text-foreground shadow">
          <span>{label}</span>
          <span className="text-muted-foreground">{confidencePercent}</span>
        </div>
      )}
      {progressFeedback && (
        <span className="text-xs text-muted-foreground">{progressFeedback}</span>
      )}
    </div>
  );
}
